import {
  getInvoiceActionPolicy,
  INVOICE_ACTION,
  INVOICE_STATUS,
  canReissue,
  normalizeInvoiceStatusKey,
  type InvoiceAction,
  type InvoiceActionPolicy,
} from '@aid/shared';
import type { CobroClienteResponse } from '@aid/shared';
import type { StatusTagTone } from '../../../components/StatusTag';
import {
  FACTURA_STATUS_KEY,
  FACTURA_TABLE_GROUP,
  FACTURA_TABLE_SCOPE,
} from '../components/factura-table-types';
import type {
  FacturaStatusKey,
  FacturaTableFilterState,
  FacturaTableGroup,
  FacturaTableGroupBy,
  FacturaTableQueryFilters,
  FacturaTableScope,
  FacturasTranslate,
} from '../components/factura-table-types';

export function getFacturaInitials(nombre: string | null | undefined): string {
  const words = nombre?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return '#';
  return words
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

/** Resolve the persisted catalog key; missing status is explicit, never guessed as pending. */
export function getFacturaStatusKey(
  factura: Pick<CobroClienteResponse, 'estado' | 'motivoAnulacion'>,
): FacturaStatusKey {
  const key = normalizeInvoiceStatusKey(factura.estado?.key);
  if (key === INVOICE_STATUS.VIGENTE) return FACTURA_STATUS_KEY.PENDIENTE;
  if (key === INVOICE_STATUS.PAGADA) return FACTURA_STATUS_KEY.PAGADA;
  if (key === INVOICE_STATUS.ANULADA) return FACTURA_STATUS_KEY.ANULADA;
  if (factura.motivoAnulacion) return FACTURA_STATUS_KEY.ANULADA;
  return FACTURA_STATUS_KEY.DESCONOCIDA;
}

export function getFacturaActionPolicy(factura: CobroClienteResponse): InvoiceActionPolicy {
  const statusKey = getFacturaStatusKey(factura);
  return getInvoiceActionPolicy({
    status:
      statusKey === FACTURA_STATUS_KEY.DESCONOCIDA ? null : normalizeInvoiceStatusKey(statusKey),
    origin: factura.generadoPor,
  });
}

/**
 * Presentation menu model for the facturas table secondary actions. Invoice
 * corrections only use reissue or annulment (R2 removed edit). `annul` is
 * driven by `getInvoiceActionPolicy().annul` (visible for any known status,
 * enabled for non-terminal); `reissue` by the pure display-only `canReissue`
 * gate (the backend re-checks the window/package authoritatively).
 */
export interface FacturaActionMenuItem {
  key: InvoiceAction;
  visible: boolean;
  enabled: boolean;
}

export interface FacturaActionMenuOptions {
  /** Business-calendar base date (`YYYY-MM-DD`) for the re-issue window. */
  fechaBase: string;
}

export function getFacturaActionMenu(
  factura: CobroClienteResponse,
  options: FacturaActionMenuOptions,
): FacturaActionMenuItem[] {
  const policy = getFacturaActionPolicy(factura);
  const reissueGate = canReissue(
    {
      id: String(factura.id),
      estado: normalizeInvoiceStatusKey(factura.estado?.key),
      fechaInicio: factura.fechaInicio,
      linkedToPrepaid: factura.linkedToPrepaid ?? false,
    },
    { diaCobro: factura.diaCobro ?? null },
    options.fechaBase,
  );
  const statusKey = getFacturaStatusKey(factura);
  const isAnnulled = statusKey === FACTURA_STATUS_KEY.ANULADA;
  return [
    // ANNUL is hidden for ANULADA (terminal state); shown and enabled for
    // VIGENTE and PAGADA. The shared policy keeps `visible=true` for any
    // non-null status, but the product intent is to hide the action entirely
    // once the invoice is annulled.
    { key: INVOICE_ACTION.ANNUL, visible: policy.annul.visible && !isAnnulled, enabled: policy.annul.enabled },
    { key: INVOICE_ACTION.REISSUE, visible: reissueGate, enabled: reissueGate },
  ];
}

export function getFacturaStatusLabel(
  key: string | null | undefined,
  t: FacturasTranslate,
): string {
  if (key === FACTURA_STATUS_KEY.PENDIENTE) return t('pages.cobros.estadoVigente');
  if (key === FACTURA_STATUS_KEY.PAGADA) return t('pages.cobros.estadoPagada');
  if (key === FACTURA_STATUS_KEY.ANULADA) return t('pages.cobros.estadoAnulado');
  return t('pages.cobros.estadoDesconocido');
}

const FACTURA_STATUS_TONES = {
  [FACTURA_STATUS_KEY.PENDIENTE]: 'warning',
  [FACTURA_STATUS_KEY.PAGADA]: 'success',
  [FACTURA_STATUS_KEY.ANULADA]: 'error',
  [FACTURA_STATUS_KEY.DESCONOCIDA]: 'neutral',
} satisfies Record<FacturaStatusKey, StatusTagTone>;

export function getFacturaStatusTone(key: FacturaStatusKey): StatusTagTone {
  return FACTURA_STATUS_TONES[key];
}

export function buildFacturasQueryFilters(
  scope: FacturaTableScope,
  filters: FacturaTableFilterState,
  clienteId?: number,
): FacturaTableQueryFilters {
  const queryFilters: FacturaTableQueryFilters = {};
  const typedClientId =
    scope === FACTURA_TABLE_SCOPE.CLIENTE ? clienteId : Number(filters.idCliente);
  const hasClientId = scope === FACTURA_TABLE_SCOPE.CLIENTE || filters.idCliente.trim() !== '';

  if (hasClientId && Number.isInteger(typedClientId) && Number(typedClientId) > 0) {
    queryFilters.idCliente = typedClientId;
  }
  const search = filters.search.trim();
  if (search !== '') queryFilters.search = search;
  if (filters.tipoFactura !== undefined) queryFilters.tipoFactura = filters.tipoFactura;
  if (filters.idEstado !== undefined) queryFilters.idEstado = filters.idEstado;
  if (filters.fechaDesde !== undefined) queryFilters.fechaDesde = filters.fechaDesde;
  if (filters.fechaHasta !== undefined) queryFilters.fechaHasta = filters.fechaHasta;

  return queryFilters;
}

export function groupFacturas(
  rows: CobroClienteResponse[],
  groupBy: FacturaTableGroupBy,
  t: FacturasTranslate,
): FacturaTableGroup[] | null {
  if (groupBy === FACTURA_TABLE_GROUP.NONE) return null;

  const groups = rows.reduce((groupMap, factura) => {
    const label =
      groupBy === FACTURA_TABLE_GROUP.CLIENTE
        ? factura.cliente
          ? `${factura.cliente.nombre ?? ''} (#${factura.cliente.id})`
          : `#${factura.idCliente}`
        : getFacturaStatusLabel(getFacturaStatusKey(factura), t);
    const groupRows = groupMap.get(label);
    if (groupRows) groupRows.push(factura);
    else groupMap.set(label, [factura]);
    return groupMap;
  }, new Map<string, CobroClienteResponse[]>());

  return Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right, 'es'))
    .map(([label, groupedRows]) => ({ label, rows: groupedRows }));
}
